/**
 * Version helpers for the Studio.
 *
 * `Song` has no "current version" field: `addVersion` appends, and
 * `restoreVersion` only copies a version's length onto the song without recording
 * which one is live. So the redesign *derives* what it can rather than inventing
 * state — the most recent version is the last entry, and that is what the Studio
 * labels "Latest". It deliberately does not claim to be "current", because after a
 * restore that would be a guess.
 *
 * When the backend starts tracking this (a `currentVersionId` on the song), these
 * two functions are where the UI switches over; nothing else reads the array
 * positionally.
 */
import type { Song, SongVersion } from '../types/domain';

export interface SplitVersionLabelParts {
  key: string;
  part: 1 | 2;
}

export interface SplitVersionPair {
  key: string;
  first: SongVersion;
  second: SongVersion;
}

/** The most recently added version, or null for a song with no versions. */
export function latestVersion(song: Song): SongVersion | null {
  return song.versions.length > 0 ? song.versions[song.versions.length - 1] : null;
}

/**
 * Parses the split label convention the Studio writes into Version History.
 *
 * Split labels are the only version rows the Edit → Split follow-up Crossfade can safely
 * target, because those ids map to the hidden backing songs that already have standalone
 * audio assets. The optional `Version N · ` prefix is display-only and must be ignored when
 * pairing the two halves back up.
 */
export function splitLabelParts(label: string): SplitVersionLabelParts | null {
  const match = label.match(/^(?:Version \d+ · )?(.*) \(Part ([12])\)$/);
  if (!match) return null;
  return {
    key: match[1] ?? label,
    part: match[2] === '1' ? 1 : 2,
  };
}

/**
 * All valid split pairs in Version History, newest last to match the source array.
 *
 * A pair is only valid when two adjacent `kind: 'split'` rows share the same base label and
 * are ordered Part 1 then Part 2. Anything orphaned, mismatched or separated by another
 * version is treated as history noise rather than guessed into a pair.
 */
export function splitVersionPairs(song: Song): SplitVersionPair[] {
  const pairs: SplitVersionPair[] = [];
  for (let i = 0; i < song.versions.length - 1; i += 1) {
    const first = song.versions[i];
    const second = song.versions[i + 1];
    if (!first || !second || first.kind !== 'split' || second.kind !== 'split') continue;
    const firstParts = splitLabelParts(first.label);
    const secondParts = splitLabelParts(second.label);
    if (!firstParts || !secondParts) continue;
    if (firstParts.key !== secondParts.key || firstParts.part !== 1 || secondParts.part !== 2) continue;
    pairs.push({ key: firstParts.key, first, second });
  }
  return pairs;
}

/** The newest valid split pair in Version History, or null when none exists. */
export function latestSplitVersionPair(song: Song): SplitVersionPair | null {
  const pairs = splitVersionPairs(song);
  return pairs.length > 0 ? pairs[pairs.length - 1] : null;
}

/**
 * The title the Studio shows for a loaded version.
 *
 * Matches the existing convention the hidden version-backed songs and "Save to library"
 * already use, so a version loaded in-place is named exactly the same way it would be if
 * promoted to its own library row.
 */
export function versionTitle(song: Song, version: SongVersion): string {
  return `${song.title} — ${version.label}`;
}

/**
 * A Studio-facing song view for one loaded version.
 *
 * The hidden backing song, when one exists, is the truthful source for fields a version row
 * cannot carry on its own — notably BPM after a tempo edit. But that backing record is not a
 * library song the user owns separately: cover art, favourite state, counts, visibility and
 * Version History still belong to the visible parent song. So the Studio renders a merged view
 * rather than the hidden row raw.
 */
export function versionViewSong(song: Song, version: SongVersion, versionSong: Song | null = null): Song {
  const plainOriginal = version.kind === 'original' && versionSong === null;
  return {
    ...song,
    id: version.id,
    seq: versionSong?.seq ?? song.seq,
    title: versionSong?.title ?? versionTitle(song, version),
    length: plainOriginal ? song.length : (versionSong?.length ?? version.length),
    date: plainOriginal ? song.date : (versionSong?.date ?? version.date),
    createdAt: plainOriginal ? (song.createdAt ?? version.createdAt) : (versionSong?.createdAt ?? version.createdAt ?? song.createdAt),
    lyrics: plainOriginal ? song.lyrics : (versionSong?.lyrics ?? version.lyrics ?? song.lyrics),
    lyricsTimedRaw:
      plainOriginal
        ? song.lyricsTimedRaw
        : versionSong?.lyricsTimedRaw !== undefined
          ? versionSong.lyricsTimedRaw
          : version.lyricsTimedRaw !== undefined
            ? version.lyricsTimedRaw
            : song.lyricsTimedRaw,
    lyricTimings:
      plainOriginal
        ? song.lyricTimings
        : versionSong?.lyricTimings !== undefined
          ? versionSong.lyricTimings
          : version.lyricTimings !== undefined
            ? version.lyricTimings
            : song.lyricTimings,
    lyricSectionTimings:
      plainOriginal
        ? song.lyricSectionTimings
        : versionSong?.lyricSectionTimings !== undefined
          ? versionSong.lyricSectionTimings
          : version.lyricSectionTimings !== undefined
            ? version.lyricSectionTimings
            : song.lyricSectionTimings,
    prompt: plainOriginal ? song.prompt : (versionSong?.prompt ?? version.prompt ?? song.prompt),
    bpm: versionSong?.bpm ?? song.bpm,
    mood: versionSong?.mood ?? song.mood,
    language: versionSong?.language ?? song.language,
    aiModel: versionSong?.aiModel ?? song.aiModel,
    creditsUsed: versionSong?.creditsUsed ?? song.creditsUsed,
    songOrigin: versionSong?.songOrigin ?? song.songOrigin,
    audioUrl:
      versionSong?.audioUrl !== undefined
        ? versionSong.audioUrl
        : version.audioUrl !== undefined
          ? version.audioUrl
          : song.audioUrl,
    audioProvenance: versionSong?.audioProvenance ?? version.audioProvenance ?? song.audioProvenance,
    providerClipId: versionSong?.providerClipId ?? version.providerClipId ?? song.providerClipId,
    // Keep artwork and song-level state anchored to the visible library song rather than the
    // hidden backing row, which deliberately does not own them independently.
    coverUrl: song.coverUrl,
    artworkSeed: song.artworkSeed ?? song.id,
    coverStyle: song.coverStyle,
    coverSource: song.coverSource,
    favorite: song.favorite,
    downloadCount: song.downloadCount,
    playCount: song.playCount,
    loveCount: song.loveCount,
    shareCount: song.shareCount,
    versions: song.versions,
    visibility: song.visibility,
    parentSongId: song.id,
  };
}

/**
 * The short form of a version label, for badges: "Version 3 · Remix" → "Version 3".
 *
 * `addVersion` composes labels as `Version N · what changed`; the seeded first
 * version is plain "Original", and a renamed version is whatever the user typed.
 * All three come back sensibly from splitting on the separator.
 */
export function versionShortLabel(version: SongVersion): string {
  return version.label.split(' · ')[0];
}
